import React, { useState } from 'react'
import PropTypes from 'prop-types'
import request from 'superagent'
import { withRouter } from 'react-router-dom'
import { Link } from 'react-router-dom'
import CopyToClipboard from 'react-copy-to-clipboard'
import { css } from '@emotion/react'

import { confirmDestroy } from '~/utils/alert.js'
import get from '~/utils/paginated.js'
import ErrorUI from '~/webhooks/components/error.jsx'
import Loading from '~/webhooks/components/loading.jsx'
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Collapse,
  Heading,
  Input,
  Logo,
  StackView,
  Text,
  TileView,
} from '@planningcenter/tapestry-react'
import { ThemeProvider } from '~/components/ThemeProvider'
import { PageHeader } from '~/components/tapestry'
import { token } from '@planningcenter/tapestry'

const formatLabel = (word) => {
  return (
    word[0].toUpperCase() + word.slice(1).toLowerCase().replaceAll('_', ' ')
  )
}

const mapEvents = (events) => {
  let eventsObj = {}
  for (let i = 0; i < events.length; i++) {
    const event = events[i]
    const { attributes } = event
    let tokens = attributes.name.split('.')
    // resource is the last part of the namespace before the event name.
    //  e.g., "people.v2.events.email.created" => "email"
    //
    // Some events and namespaces have different segment counts, but
    // they all contain "v2.events" between the app and the resource.
    let resourceIdx = tokens.indexOf('v2') + 2
    let resource = tokens[resourceIdx]
    let namespace = tokens.slice(0, resourceIdx + 1).join('.')
    let appObj = eventsObj[attributes.app] || {}
    let resourceObj = appObj[resource] || { namespace, events: [] }
    resourceObj.events.push(attributes.name)
    resourceObj.events.sort()
    appObj[resource] = resourceObj
    eventsObj[attributes.app] = appObj
  }
  return eventsObj
}

const EventCollectionHeader = ({ app, activeCount, handleToggle, open }) => {
  return (
    <StackView
      axis="horizontal"
      onClick={handleToggle}
      cursor="pointer"
      alignment="center"
      spacing={1}
      paddingVertical={1}
      paddingHorizontal={2}
    >
      <Button
        theme="default"
        onClick={handleToggle}
        title={open ? 'Collapse section' : 'Expand section'}
        icon={{
          name: open ? 'general.downChevron' : 'general.rightChevron',
          fill: 'grey-8',
          size: 1.5,
        }}
        css={{
          minWidth: 0,
          minHeight: 0,
          background: 'transparent',
          ':hover': { background: 'transparent' },
        }}
      />
      <Logo name={app} size="lg" theme="color" label={formatLabel(app)} />
      {activeCount > 0 ? (
        <Badge
          title={`${activeCount} selected`}
          color={{
            background: token('--t-fill-color-status-info-ghost'),
            foreground: token('--t-text-color-status-info'),
          }}
          marginLeft="auto"
          size="xs"
          radius="pill"
        />
      ) : null}
    </StackView>
  )
}

const EventCollectionResource = React.memo((props) => {
  const { resource, resources } = props
  const namespace = resources[resource].namespace

  return (
    <StackView
      as="li"
      axis="horizontal"
      borderTop="1px solid"
      borderColor="grey-3"
      paddingHorizontal={2}
      paddingVertical={0.5}
      spacing={2}
    >
      <Box minWidth={30} paddingVertical={0.5}>
        <Heading
          level={4}
          size={16}
          fontWeight={500}
          color="grey-9"
          marginBottom={0}
        >
          {formatLabel(resource)}
        </Heading>
        <Text size={11} lineHeight="1" maxWidth="240px" wordBreak="break-word">
          {namespace}.*
        </Text>
      </Box>
      <TileView
        as="ul"
        spacing={2}
        grow={1}
        paddingVertical={1}
        minCellWidth={15}
      >
        {resources[resource].events.map((event) => {
          const label = event.replace(`${namespace}.`, '')
          return (
            <EventCollectionEvent
              {...props}
              key={label}
              label={label}
              event={event}
            />
          )
        })}
      </TileView>
    </StackView>
  )
})

const EventCollectionEvent = React.memo((props) => {
  const { active, allowSave, event, label, onAdd, onRemove, open } = props
  const [isCopying, setIsCopying] = useState(false)
  const [isHovering, setIsHovering] = useState(false)

  return (
    <StackView
      as="li"
      axis="horizontal"
      alignment="center"
      spacing={1}
      onMouseEnter={() => {
        setIsHovering(true)
      }}
      onMouseLeave={() => {
        setIsHovering(false)
      }}
    >
      <StackView
        as="label"
        axis="horizontal"
        alignment="center"
        spacing={1}
        cursor="pointer"
      >
        <Checkbox
          checked={active.indexOf(event) > -1}
          disabled={!allowSave}
          label={label}
          onChange={(e) => {
            if (e.target.checked) {
              onAdd(event)
            } else {
              onRemove(event)
            }
          }}
          size="md"
          tabIndex={!open ? -1 : undefined}
        />
      </StackView>
      <CopyToClipboard
        text={event}
        onCopy={() => {
          setIsCopying(true)
          setTimeout(() => setIsCopying(false), 1000)
        }}
      >
        <Button
          opacity={isHovering ? 1 : 0}
          disabled={isCopying}
          onFocus={() => {
            setIsHovering(true)
          }}
          onBlur={() => {
            setIsHovering(false)
          }}
          iconLeft={{
            name: isCopying ? 'general.check' : 'general.outlinedTwoBlankFiles',
            color: isCopying ? 'success' : 'grey-7',
            size: '12px',
            marginRight: 0.5,
          }}
          tabIndex={!open ? -1 : undefined}
          title={isCopying ? 'copied' : 'copy'}
          aria-label={`Copy ${event} to clipboard`}
          tooltip={{
            placement: 'top',
            title: `Copy full event name to clipboard`,
          }}
          color="grey-9"
          fontSize={10}
          fontWeight={300}
          lineHeight="1"
          minWidth={0}
          minHeight={0}
          css={{
            background: 'transparent',
            padding: '4px',
            ':focus-visible': { outline: 'revert !important' },
            ':hover': { background: 'transparent' },
          }}
        />
      </CopyToClipboard>
    </StackView>
  )
})

const EventCollection = (props) => {
  const { app, active, allowDelete, resources } = props
  const activeCount = active.filter((event) => event.includes(`${app}.`)).length
  // collapse group if existing subscription with no active events in this section
  const [open, setOpen] = useState(allowDelete && !activeCount ? false : true)

  return (
    <StackView
      marginBottom={2}
      border="1px solid"
      borderColor="grey-3"
      radius={4}
    >
      <EventCollectionHeader
        app={app}
        activeCount={activeCount}
        handleToggle={() => setOpen(!open)}
        open={open}
      />
      <Collapse open={open}>
        <StackView as="ul" margin={0}>
          {Object.keys(resources)
            .sort()
            .map((resource) => {
              return (
                <EventCollectionResource
                  {...props}
                  key={resource}
                  resource={resource}
                  open={open}
                />
              )
            })}
        </StackView>
      </Collapse>
    </StackView>
  )
}

const Events = (props) => {
  const { events, onChange, ...rest } = props
  return (
    <>
      {Object.keys(events).map((key) => {
        return (
          <EventCollection
            key={key}
            app={key}
            resources={events[key]}
            {...rest}
          />
        )
      })}
    </>
  )
}

class SubscriptionEditor extends React.Component {
  static propTypes = {
    active: PropTypes.array,
    url: PropTypes.string,
    name: PropTypes.string.isRequired,
    returnTo: PropTypes.string.isRequired,
    allowSave: PropTypes.bool.isRequired,
    allowDelete: PropTypes.bool.isRequired,
  }

  static defaultProps = {
    active: [],
    url: '',
    allowDelete: false,
  }

  state = {
    isLoading: true,
    isSaving: false,
    loadError: null,
    saveError: null,
    active: this.props.active,
    url: this.props.url,
    events: {},
  }

  componentDidMount() {
    get('/webhooks/v2/available_events?per_page=100')
      .then((events) => {
        this.setState({
          events: mapEvents(events),
          isLoading: false,
        })
      })
      .catch((error) => {
        this.setState({ loadError: error.message })
      })
  }

  _submit = (event) => {
    // Sometimes in Safari the form will submit, sometimes it won't.  I has a sad
    event.preventDefault()

    const { active, url } = this.state
    const payload = {
      data: {
        attributes: {
          url: url,
          old_url: this.props.url,
          names: active,
        },
      },
    }

    this._sendPayload(payload)
  }

  _sendPayload = (payload) => {
    this.setState({ isSaving: true, saveError: null })

    request
      .post('/webhooks/v2/batch_update')
      .set(
        'X-CSRF-Token',
        document
          .querySelector('meta[name="csrf-token"]')
          .getAttribute('content')
      )
      .send(payload)
      .end((error, response) => {
        if (error) {
          this.setState({ saveError: { error, response }, isSaving: false })
        } else {
          this.props.history.push(this.props.returnTo)
        }
      })
  }

  _addEvent = (name) => {
    let active = this.state.active.slice(0)
    active.push(name)
    this.setState({ active })
  }

  _removeEvent = (name) => {
    let active = this.state.active.slice(0)
    let index = active.indexOf(name)
    if (index > -1) {
      active.splice(index, 1)
      this.setState({ active })
    }
  }

  _delete = () => {
    const { url } = this.state
    const deleteAll = () => {
      const payload = {
        data: {
          attributes: {
            url: url,
            names: [],
          },
        },
      }
      this._sendPayload(payload)
    }
    confirmDestroy
      .fire({
        title: 'Delete this webhook?',
        text: 'Are you sure you want to delete this webhook?',
        confirmButtonText: 'Yes, delete webhook',
      })
      .then(({ isConfirmed }) => {
        if (isConfirmed) deleteAll()
      })
  }

  render() {
    return (
      <ThemeProvider>
        <ErrorUI message={this.state.loadError}>
          <PageHeader
            breadcrumb={
              <Box as={Link} to={this.props.returnTo}>
                Back to webhooks
              </Box>
            }
          >
            <h1>{this.props.name}</h1>
          </PageHeader>

          <div className="main-body">
            <div className="main-content w-100% xw-960p mx-a">
              <Loading isLoading={this.state.isLoading || this.state.isSaving}>
                <form onSubmit={this._submit}>
                  <Box padding={token('--t-spacing-2')}>
                    <StackView marginBottom={2} spacing={3}>
                      <SaveError {...this.state.saveError} />
                      <StackView spacing={0.5}>
                        <Input.InputLabel
                          fontWeight={500}
                          color="grey-9"
                          cursor="default"
                          controls="subscription_url"
                        >
                          Endpoint URL
                        </Input.InputLabel>
                        <Text color="grey-7" marginBottom={1}>
                          Enter the URL where you would like to receive a POST
                          request when the selected type of event occurs. More
                          details about webhooks are available in our{' '}
                          <a href="/docs/overview/webhooks">
                            documentation
                          </a>
                          .
                        </Text>
                        <Input
                          id="subscription_url"
                          type="url"
                          placeholder="https://example.com/webhook"
                          color="grey-9"
                          value={this.state.url}
                          disabled={!this.props.allowSave}
                          onChange={(e) => {
                            this.setState({ url: e.target.value })
                          }}
                        />
                      </StackView>
                      <StackView spacing={1} marginTop={1}>
                        <Heading
                          level={2}
                          size={14}
                          color="grey-9"
                          fontWeight={500}
                          lineHeight="1"
                        >
                          Events to listen for
                        </Heading>
                        <Events
                          events={this.state.events}
                          active={this.state.active}
                          onAdd={this._addEvent}
                          onRemove={this._removeEvent}
                          allowSave={this.props.allowSave}
                          allowDelete={this.props.allowDelete}
                        />
                      </StackView>
                      <StackView
                        axis="horizontal"
                        spacing={2}
                        alignment="center"
                        distribution="end"
                      >
                        {this.props.allowDelete ? (
                          <Box flex={1}>
                            <Button
                              theme="delete"
                              variant="outline"
                              size="md"
                              onClick={this._delete}
                            >
                              Delete
                            </Button>
                          </Box>
                        ) : null}
                        <Button
                          to="#/"
                          theme="default"
                          variant="naked"
                          size="md"
                        >
                          Cancel
                        </Button>
                        {this.props.allowSave ? (
                          <Button
                            type="submit"
                            theme="interaction"
                            variant="fill"
                            size="md"
                            title="Save"
                            onClick={this._submit}
                          />
                        ) : null}
                      </StackView>
                    </StackView>
                  </Box>
                </form>
              </Loading>
            </div>
          </div>
        </ErrorUI>
      </ThemeProvider>
    )
  }
}

const SaveError = ({ error, response }) => {
  if (!error || !response) {
    return null
  }
  const style = {
    fontWeight: 'bold',
    color: '#c24430',
    border: 'solid 2px #ff2600',
    backgroundColor: '#ffede9',
    padding: '8px 16px',
  }
  if (response.status === 422 && response.body.errors) {
    const { errors } = response.body
    return (
      <Box>
        {errors.map((error) => {
          return (
            <Text style={style} key={error.detail}>
              {error.detail}
            </Text>
          )
        })}
      </Box>
    )
  }
  return <Box style={style}>Oops... {error.message}</Box>
}

export default withRouter(SubscriptionEditor)
